import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import earthquakeTableExcel from '../assets/excels/【訂正版】初動対応俯瞰表（地震）.xlsx';
import floodTableExcel from '../assets/excels/【訂正版】初動対応俯瞰表（水害）.xlsx';
import infectionTableExcel from '../assets/excels/【訂正版】初動対応俯瞰表（感染症）.xlsx';
import procedureManualExcel from '../assets/excels/手順書（SONAE-AI）.xlsx';
import Spinner from './Spinner/Spinner';
import { bcpService } from '../services/bcpService';
import { saveAllDraftSections, loadBasicBcpDraft, getInitialResponseDefaultValues, saveBasicBcpSection, getDefaultValuesFromSections } from '../utils/basicBcpStorage';
import { INITIAL_RESPONSE_SECTIONS } from '../config/initialResponseSections';
import { RESTORE_POLICY_SECTIONS } from '../config/restorePolicySections';
import { REGULATION_SECTIONS } from '../config/regulationSections';
import { PROCEDURE_MANUAL_SECTIONS } from '../config/procedureManualSections';

interface DownloadItem {
  title: string;
  description: string;
  formats: Array<{
    type: 'PDF' | 'Word' | 'Excel';
    color: string;
  }>;
  sectionKey?: string; // BCPデータのセクションキー。静的ファイルの場合はundefined
  isStatic?: boolean; // 静的ファイル（様式）の場合true
}

interface BCPDownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
  bcpType: 'introductory' | 'simple' | 'basic';
  bcpId?: string | null;
  tenantId?: string | null;
}

const DOWNLOAD_ITEMS: Record<string, DownloadItem[]> = {
  introductory: [
    {
      title: '超入門版BCPシート',
      description: '首都直下地震発生時に最低限検討すべき項目をまとめたBCP',
      formats: [
        { type: 'PDF', color: '#DC2626' },
        { type: 'Word', color: '#2563EB' },
      ],
    },
  ],
  simple: [
    {
      title: '簡易版BCPシート',
      description: 'あらゆるリスクに耐えられる想定をした簡易的なオールハザード型BCP',
      formats: [
        { type: 'PDF', color: '#DC2626' },
        { type: 'Word', color: '#2563EB' },
      ],
    },
  ],
  basic: [
    {
      title: '復旧方針書',
      description: 'BCP目標設定、経営資源の整理、復旧戦略をまとめた書類',
      formats: [{ type: 'Word', color: '#2563EB' }],
      sectionKey: 'restore_policy',
    },
    {
      title: 'BCP規程',
      description: '基本方針、平常時の取り組み、緊急時の対応体制をまとめた規程',
      formats: [{ type: 'Word', color: '#2563EB' }],
      sectionKey: 'regulation',
    },
    {
      title: '【地震】初動対応サマリー',
      description: '地震発生時の初動対応手順をまとめたサマリー',
      formats: [{ type: 'Word', color: '#2563EB' }],
      sectionKey: 'earthquake_summary',
    },
    {
      title: '【地震】初動対応俯瞰表(様式)',
      description: '地震発生時の詳細な初動対応手順表(編集可能な様式)',
      formats: [{ type: 'Excel', color: '#15803D' }],
      isStatic: true,
    },
    {
      title: '【水害】初動対応サマリー',
      description: '水害発生時の初動対応手順をまとめたサマリー',
      formats: [{ type: 'Word', color: '#2563EB' }],
      sectionKey: 'flood_summary',
    },
    {
      title: '【水害】初動対俯瞰表(様式)',
      description: '水害発生時の詳細な初動対応手順表(編集可能な様式)',
      formats: [{ type: 'Excel', color: '#15803D' }],
      isStatic: true,
    },
    {
      title: '【感染症】初動対応サマリー',
      description: '感染症発生時の初動対応手順をまとめたサマリー',
      formats: [{ type: 'Word', color: '#2563EB' }],
      sectionKey: 'infection_summary',
    },
    {
      title: '【感染症】初動俯瞰表(様式)',
      description: '感染症発生時の詳細な初動対応手順表(編集可能な様式)',
      formats: [{ type: 'Excel', color: '#15803D' }],
      isStatic: true,
    },
    {
      title: '手順書(様式)',
      description: '各種対応手順を記載するための様式',
      formats: [{ type: 'Excel', color: '#15803D' }],
      isStatic: true,
    },
    {
      title: '手順書(備蓄品リスト)',
      description: '災害時に必要な備蓄品のリスト(参考用)',
      formats: [{ type: 'Word', color: '#2563EB' }],
      sectionKey: 'procedure_manual',
    },
  ],
};

const MODAL_TITLES: Record<string, string> = {
  introductory: '超入門版BCP ダウンロード',
  simple: '簡易版BCP ダウンロード',
  basic: '基本版BCP ダウンロード',
};

const BCPDownloadModal: React.FC<BCPDownloadModalProps> = ({
  isOpen,
  onClose,
  bcpType,
  bcpId,
  tenantId: tenantIdProp,
}) => {
  const [searchParams] = useSearchParams();
  const tenantIdFromUrl = searchParams.get('tenantId');
  // Priority: props > URL parameter > null
  const effectiveTenantId = tenantIdProp || tenantIdFromUrl || null;
  // ローディング状態管理
  const [downloadingItem, setDownloadingItem] = useState<string | null>(null);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [isDownloadingSummary, setIsDownloadingSummary] = useState(false);
  const [bcpData, setBcpData] = useState<any>(null);
  const [createdSections, setCreatedSections] = useState<Set<string>>(new Set());

  // BCPデータを取得して、どのセクションが生成済みかを判定
  // モーダルが開くたびに最新のBCPデータを取得
  useEffect(() => {
    console.log('[BCPDownloadModal] useEffect triggered');
    console.log('[BCPDownloadModal] isOpen:', isOpen);
    console.log('[BCPDownloadModal] bcpId:', bcpId);
    console.log('[BCPDownloadModal] bcpType:', bcpType);

    if (!isOpen || !bcpId || bcpType !== 'basic') {
      console.log('[BCPDownloadModal] Skipping fetch - condition not met');
      return;
    }

    const fetchBcpData = async () => {
      console.log('[BCPDownloadModal] Fetching BCP data...');
      try {
        const data = await bcpService.getBCP(bcpId);
        setBcpData(data);

        console.log('[BCPDownloadModal] Full BCP data:', data);
        console.log('[BCPDownloadModal] content:', data.content);
        console.log('[BCPDownloadModal] content.sections:', data.content?.sections);

        // content内のどのセクションにデータがあるかをチェック
        const sections = new Set<string>();
        if (data.content) {
          // まず content.sections を優先的にチェック
          const contentSections = data.content.sections || {};

          // restore_policy: sectionsまたは直接フィールドをチェック
          const restorePolicySection = contentSections.restore_policy;
          console.log('[BCPDownloadModal] restore_policy section:', restorePolicySection);
          console.log('[BCPDownloadModal] restore_policy keys:', restorePolicySection ? Object.keys(restorePolicySection) : 'null/undefined');

          if (restorePolicySection && Object.keys(restorePolicySection).length > 0) {
            console.log('[BCPDownloadModal] ✓ restore_policy section has data');
            sections.add('restore_policy');
          } else if (data.content.bcpObjective || data.content.managementResources || data.content.recoveryStrategy) {
            console.log('[BCPDownloadModal] ✓ restore_policy fallback fields found');
            sections.add('restore_policy');
          } else {
            console.log('[BCPDownloadModal] ✗ restore_policy section NOT detected');
          }

          // regulation: sectionsまたは直接フィールドをチェック
          const regulationSection = contentSections.regulation;
          if (regulationSection && Object.keys(regulationSection).length > 0) {
            sections.add('regulation');
          } else if (data.content.basicPolicy || data.content.peacetimeEfforts || data.content.emergencyResponse) {
            sections.add('regulation');
          }

          // initial_response: sectionsまたは直接フィールドをチェック
          const initialResponseSection = contentSections.initial_response;
          if (initialResponseSection && Object.keys(initialResponseSection).length > 0) {
            sections.add('initial_response');
            // 個別のサマリーフィールドもチェック（ガイドラインまたはレスポンスのどちらかがあればOK）
            if (initialResponseSection.earthquakeGuidelines || initialResponseSection.earthquakeResponse) {
              sections.add('earthquake_summary');
            }
            if (initialResponseSection.floodGuidelines || initialResponseSection.floodResponse) {
              sections.add('flood_summary');
            }
            if (initialResponseSection.infectionGuidelines || initialResponseSection.infectionResponse) {
              sections.add('infection_summary');
            }
          } else if (data.content.earthquakeResponse || data.content.floodResponse || data.content.infectionResponse || data.content.earthquakeGuidelines || data.content.floodGuidelines || data.content.infectionGuidelines) {
            sections.add('initial_response');
            // レガシーフォーマットの場合も個別にチェック（ガイドラインまたはレスポンスのどちらかがあればOK）
            if (data.content.earthquakeGuidelines || data.content.earthquakeResponse) {
              sections.add('earthquake_summary');
            }
            if (data.content.floodGuidelines || data.content.floodResponse) {
              sections.add('flood_summary');
            }
            if (data.content.infectionGuidelines || data.content.infectionResponse) {
              sections.add('infection_summary');
            }
          }

          // procedure_manual: sectionsまたは直接フィールドをチェック
          const procedureManualSection = contentSections.procedure_manual;
          if (procedureManualSection && Object.keys(procedureManualSection).length > 0) {
            sections.add('procedure_manual');
          } else if (data.content.stockpileList || data.content.stockpilePlan || data.content.procedureManual) {
            sections.add('procedure_manual');
          }
        }

        // localStorageからドラフトデータをチェックして、未保存のセクションも含める
        const draft = loadBasicBcpDraft(bcpId, effectiveTenantId);
        if (draft && draft.sections) {
          Object.keys(draft.sections).forEach(sectionKey => {
            const sectionData = draft.sections[sectionKey as keyof typeof draft.sections];
            if (sectionData && Object.keys(sectionData).length > 0) {
              console.log(`[BCPDownloadModal] ✓ Draft data found for section: ${sectionKey}`);
              sections.add(sectionKey);

              // initial_responseセクションの場合、個別のサマリーもチェック（ガイドラインまたはレスポンスのどちらかがあればOK）
              if (sectionKey === 'initial_response') {
                if (sectionData.earthquakeGuidelines || sectionData.earthquakeResponse) {
                  sections.add('earthquake_summary');
                }
                if (sectionData.floodGuidelines || sectionData.floodResponse) {
                  sections.add('flood_summary');
                }
                if (sectionData.infectionGuidelines || sectionData.infectionResponse) {
                  sections.add('infection_summary');
                }
              }
            }
          });
        }

        // 初動対応セクションは、デフォルト値が設定されているので常に有効にする
        // INITIAL_RESPONSE_SECTIONSから各災害タイプのデフォルト値の存在を確認
        const earthquakeFields = INITIAL_RESPONSE_SECTIONS.find(s => s.id === 'earthquake')?.fields || [];
        const floodFields = INITIAL_RESPONSE_SECTIONS.find(s => s.id === 'flood')?.fields || [];
        const infectionFields = INITIAL_RESPONSE_SECTIONS.find(s => s.id === 'infection')?.fields || [];

        // 各災害タイプで、ガイドラインまたはレスポンスフィールドにデフォルト値があれば有効
        const hasEarthquakeDefault = earthquakeFields.some(f =>
          (f.id === 'earthquakeGuidelines' || f.id === 'earthquakeResponse') && f.defaultValue
        );
        const hasFloodDefault = floodFields.some(f =>
          (f.id === 'floodGuidelines' || f.id === 'floodResponse') && f.defaultValue
        );
        const hasInfectionDefault = infectionFields.some(f =>
          (f.id === 'infectionGuidelines' || f.id === 'infectionResponse') && f.defaultValue
        );

        if (hasEarthquakeDefault) {
          console.log('[BCPDownloadModal] ✓ earthquake_summary enabled (has default value)');
          sections.add('earthquake_summary');
        }
        if (hasFloodDefault) {
          console.log('[BCPDownloadModal] ✓ flood_summary enabled (has default value)');
          sections.add('flood_summary');
        }
        if (hasInfectionDefault) {
          console.log('[BCPDownloadModal] ✓ infection_summary enabled (has default value)');
          sections.add('infection_summary');
        }

        // BCP規程セクションは、デフォルト値が設定されているので常に有効にする
        const regulationHasDefaults = REGULATION_SECTIONS.some(section =>
          section.fields.some(field => field.type !== 'static' && field.defaultValue)
        );
        if (regulationHasDefaults) {
          console.log('[BCPDownloadModal] ✓ regulation enabled (has default values)');
          sections.add('regulation');
        }

        // 手順書セクションは、デフォルト値がなくても常に有効にする
        // (ダウンロード時にデフォルト値がマージされるため)
        console.log('[BCPDownloadModal] ✓ procedure_manual enabled (always available)');
        sections.add('procedure_manual');

        console.log('[BCPDownloadModal] Created sections (including drafts and defaults):', Array.from(sections));
        setCreatedSections(sections);
      } catch (error) {
        console.error('Failed to fetch BCP data:', error);
      }
    };

    fetchBcpData();
  }, [isOpen, bcpId, bcpType, effectiveTenantId]);

  if (!isOpen) {
    return null;
  }

  const items = DOWNLOAD_ITEMS[bcpType] || [];
  const title = MODAL_TITLES[bcpType] || 'BCP ダウンロード';

  // アイテムが生成済みかをチェック
  const isItemCreated = (item: DownloadItem) => {
    if (item.isStatic) return true; // 静的ファイルは常に利用可能
    if (!item.sectionKey) return true; // sectionKeyがない場合は利用可能とみなす
    return createdSections.has(item.sectionKey);
  };

  const handleDownload = async (itemTitle: string, format: string, item: DownloadItem) => {
    // 未生成のアイテムをチェック
    if (!isItemCreated(item)) {
      alert('このファイルはまだ生成されていません。\n該当するページで内容を入力してから、もう一度お試しください。');
      return;
    }

    const downloadKey = `${itemTitle}-${format}`;
    setDownloadingItem(downloadKey);

    try {
      console.log(`Downloading ${itemTitle} as ${format}`);

      // 基本版の処理
      if (bcpType === 'basic') {
        // Word形式の場合、ダウンロード前に未保存のセクションを自動保存
        let finalBcpId = bcpId;
        if (format === 'Word' && bcpId) {
          // Check if bcpData is loaded before auto-save
          if (!bcpData) {
            console.warn('[BCPDownloadModal] bcpData not loaded yet');
            alert('BCPデータの読み込み中です。しばらくお待ちください。');
            return;
          }

          console.log('[BCPDownloadModal] Auto-saving draft sections before individual download...');
          try {
            const tenantId = effectiveTenantId;

            // Log bcpData tenant_id details
            console.log('[BCPDownloadModal] bcpData tenant_id check (individual download):', {
              hasBcpData: !!bcpData,
              tenantIdFromUrl,
              tenant_id: bcpData?.tenant_id,
              tenantIdType: typeof bcpData?.tenant_id,
              tenantIdIsNull: bcpData?.tenant_id === null,
              tenantIdIsUndefined: bcpData?.tenant_id === undefined,
              bcpDataKeys: bcpData ? Object.keys(bcpData).slice(0, 10) : [],
              resolvedTenantIdSource: tenantIdFromUrl ? 'url' : 'bcpData',
              resolvedTenantId: tenantId
            });

            // Get default values for all 4 sections
            console.log('[BCPDownloadModal] Getting default values for all sections...');
            const restorePolicyDefaults = getDefaultValuesFromSections(RESTORE_POLICY_SECTIONS);
            const regulationDefaults = getDefaultValuesFromSections(REGULATION_SECTIONS);
            const initialResponseDefaults = getInitialResponseDefaultValues();
            const procedureManualDefaults = getDefaultValuesFromSections(PROCEDURE_MANUAL_SECTIONS);

            console.log('[BCPDownloadModal] Default values extracted:', {
              restore_policy: Object.keys(restorePolicyDefaults).length,
              regulation: Object.keys(regulationDefaults).length,
              initial_response: Object.keys(initialResponseDefaults).length,
              procedure_manual: Object.keys(procedureManualDefaults).length
            });

            // Load from localStorage (user's form input in progress)
            const draftData = loadBasicBcpDraft(bcpId, tenantId);

            // Log localStorage loading details
            const normalizedTenantId = tenantId === 'undefined' || tenantId === null || tenantId === '' ? 'default' : tenantId;
            console.log('[BCPDownloadModal] Loading localStorage (individual download):', {
              bcpId,
              tenantId,
              normalizedTenantId,
              storageKey: `basic_bcp_draft_${bcpId}_${normalizedTenantId}`,
              foundDraft: !!draftData,
              draftSections: draftData ? Object.keys(draftData.sections) : [],
              draftLastUpdated: draftData?.lastUpdated
            });

            // Load from server (already saved data)
            const serverRestorePolicy = bcpData?.content?.sections?.restore_policy || {};
            const serverRegulation = bcpData?.content?.sections?.regulation || {};
            const serverInitialResponse = bcpData?.content?.sections?.initial_response || {};
            const serverProcedureManual = bcpData?.content?.sections?.procedure_manual || {};

            // Get localStorage data
            const localRestorePolicy = draftData?.sections?.restore_policy || {};
            const localRegulation = draftData?.sections?.regulation || {};
            const localInitialResponse = draftData?.sections?.initial_response || {};
            const localProcedureManual = draftData?.sections?.procedure_manual || {};

            // Detailed log for localStorage data (before merge)
            console.log('[BCPDownloadModal] PRE-MERGE localStorage details (individual download):', {
              regulation: {
                fieldCount: Object.keys(localRegulation).length,
                fieldNames: Object.keys(localRegulation),
                sampleData: Object.keys(localRegulation).slice(0, 3).reduce((acc, key) => {
                  acc[key] = localRegulation[key]?.substring(0, 50) || localRegulation[key];
                  return acc;
                }, {} as Record<string, any>)
              },
              procedure_manual: {
                fieldCount: Object.keys(localProcedureManual).length,
                fieldNames: Object.keys(localProcedureManual),
                sampleData: Object.keys(localProcedureManual).slice(0, 3).reduce((acc, key) => {
                  acc[key] = localProcedureManual[key]?.substring(0, 50) || localProcedureManual[key];
                  return acc;
                }, {} as Record<string, any>)
              }
            });

            // Log merge sources for debugging
            console.log('[BCPDownloadModal] 3-way merge sources (individual download):', {
              hasBcpData: !!bcpData,
              serverKeys: {
                restore_policy: Object.keys(serverRestorePolicy).length,
                regulation: Object.keys(serverRegulation).length,
                initial_response: Object.keys(serverInitialResponse).length,
                procedure_manual: Object.keys(serverProcedureManual).length
              },
              localKeys: {
                restore_policy: Object.keys(localRestorePolicy).length,
                regulation: Object.keys(localRegulation).length,
                initial_response: Object.keys(localInitialResponse).length,
                procedure_manual: Object.keys(localProcedureManual).length
              },
              defaultKeys: {
                restore_policy: Object.keys(restorePolicyDefaults).length,
                regulation: Object.keys(regulationDefaults).length,
                initial_response: Object.keys(initialResponseDefaults).length,
                procedure_manual: Object.keys(procedureManualDefaults).length
              }
            });

            // Merge with priority: localStorage > server > defaults
            const mergedRestorePolicy = { ...restorePolicyDefaults, ...serverRestorePolicy, ...localRestorePolicy };
            const mergedRegulation = { ...regulationDefaults, ...serverRegulation, ...localRegulation };
            const mergedInitialResponse = { ...initialResponseDefaults, ...serverInitialResponse, ...localInitialResponse };
            const mergedProcedureManual = { ...procedureManualDefaults, ...serverProcedureManual, ...localProcedureManual };

            console.log('[BCPDownloadModal] Merged section data counts:', {
              restore_policy: Object.keys(mergedRestorePolicy).length,
              regulation: Object.keys(mergedRegulation).length,
              initial_response: Object.keys(mergedInitialResponse).length,
              procedure_manual: Object.keys(mergedProcedureManual).length
            });

            // Detailed log for merged data
            console.log('[BCPDownloadModal] POST-MERGE data details (individual download):', {
              regulation: {
                fieldCount: Object.keys(mergedRegulation).length,
                fieldNames: Object.keys(mergedRegulation),
                sampleData: Object.keys(mergedRegulation).slice(0, 3).reduce((acc, key) => {
                  acc[key] = mergedRegulation[key]?.substring(0, 50) || mergedRegulation[key];
                  return acc;
                }, {} as Record<string, any>)
              },
              procedure_manual: {
                fieldCount: Object.keys(mergedProcedureManual).length,
                fieldNames: Object.keys(mergedProcedureManual),
                sampleData: Object.keys(mergedProcedureManual).slice(0, 3).reduce((acc, key) => {
                  acc[key] = mergedProcedureManual[key]?.substring(0, 50) || mergedProcedureManual[key];
                  return acc;
                }, {} as Record<string, any>)
              }
            });

            // Save all merged data to localStorage with the BCP ID
            saveBasicBcpSection(bcpId, tenantId, 'restore_policy', mergedRestorePolicy);
            saveBasicBcpSection(bcpId, tenantId, 'regulation', mergedRegulation);
            saveBasicBcpSection(bcpId, tenantId, 'initial_response', mergedInitialResponse);
            saveBasicBcpSection(bcpId, tenantId, 'procedure_manual', mergedProcedureManual);

            console.log('[BCPDownloadModal] Saved all sections to localStorage with BCP ID:', bcpId);

            // Verify localStorage immediately after save
            const verifyData = loadBasicBcpDraft(bcpId, tenantId);
            console.log('[BCPDownloadModal] VERIFY localStorage after save (individual download):', {
              hasDraft: !!verifyData,
              sections: verifyData ? Object.keys(verifyData.sections) : [],
              sectionFieldCounts: verifyData ? {
                restore_policy: verifyData.sections?.restore_policy ? Object.keys(verifyData.sections.restore_policy).length : 0,
                regulation: verifyData.sections?.regulation ? Object.keys(verifyData.sections.regulation).length : 0,
                initial_response: verifyData.sections?.initial_response ? Object.keys(verifyData.sections.initial_response).length : 0,
                procedure_manual: verifyData.sections?.procedure_manual ? Object.keys(verifyData.sections.procedure_manual).length : 0
              } : null
            });

            finalBcpId = await saveAllDraftSections(
              bcpId,
              tenantId,
              bcpService
            );
            console.log('[BCPDownloadModal] Auto-save completed, final BCP ID:', finalBcpId);

            // 【重要】bcpDataを再取得して連続ダウンロード時のデータ巻き戻しを防止
            // 現象: saveAllDraftSections完了後、localStorageがクリアされる。
            //       その状態で2回目のダウンロードを実行すると、空のlocalStorage + 古いbcpData で
            //       3-way mergeが行われ、最新の保存内容が古いデータで上書きされてしまう。
            // 修正: 保存完了後にサーバーから最新のBCPデータを取得してbcpDataを更新することで、
            //       次回のダウンロード時に最新のサーバーデータを使用できるようにする。
            console.log('[BCPDownloadModal] Re-fetching BCP data after auto-save...');
            const updatedData = await bcpService.getBCP(finalBcpId);
            setBcpData(updatedData);
            console.log('[BCPDownloadModal] bcpData refreshed after auto-save');
          } catch (error) {
            console.error('[BCPDownloadModal] Auto-save failed:', error);
            console.warn('[BCPDownloadModal] Proceeding with download despite auto-save failure');
          }
        }
      // 様式ファイル（Excel）の場合は静的ファイルをダウンロード
      if (format === 'Excel') {
        try {
          let fileUrl = '';
          let filename = '';

          // タイトルに基づいて適切な静的ファイルを選択
          if (itemTitle === '【地震】初動対応俯瞰表(様式)') {
            fileUrl = earthquakeTableExcel;
            filename = '初動対応俯瞰表（地震）.xlsx';
          } else if (itemTitle === '【水害】初動対俯瞰表(様式)') {
            fileUrl = floodTableExcel;
            filename = '初動対応俯瞰表（水害）.xlsx';
          } else if (itemTitle === '【感染症】初動俯瞰表(様式)') {
            fileUrl = infectionTableExcel;
            filename = '初動対応俯瞰表（感染症）.xlsx';
          } else if (itemTitle === '手順書(様式)') {
            fileUrl = procedureManualExcel;
            filename = '手順書.xlsx';
          } else {
            alert('このファイルは現在対応していません。');
            return;
          }

          // 静的ファイルをダウンロード
          const a = document.createElement('a');
          a.href = fileUrl;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          console.log(`Downloaded ${itemTitle} as ${format} successfully`);
          return;
        } catch (error) {
          console.error('Download failed:', error);
          alert(`ダウンロードに失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`);
          return;
        }
      }

      // Word形式の場合はAPIから各セクションをダウンロード
      if (format === 'Word') {
        if (!finalBcpId) {
          console.error('BCP ID is not available');
          alert('BCP IDが取得できませんでした。');
          return;
        }

        try {
          let sectionName = '';

          // タイトルに基づいて適切なセクション名を選択
          if (itemTitle === '復旧方針書') {
            sectionName = 'restore-policy';
          } else if (itemTitle === 'BCP規程') {
            sectionName = 'regulation';
          } else if (itemTitle === '【地震】初動対応サマリー') {
            sectionName = 'earthquake-summary';
          } else if (itemTitle === '【水害】初動対応サマリー') {
            sectionName = 'flood-summary';
          } else if (itemTitle === '【感染症】初動対応サマリー') {
            sectionName = 'infection-summary';
          } else if (itemTitle === '手順書(備蓄品リスト)') {
            sectionName = 'stockpile-list';
          } else {
            alert('このファイルは現在対応していません。');
            return;
          }

          const apiUrl = `/api/bcp/${finalBcpId}/download/basic/section/${sectionName}`;

          const response = await fetch(apiUrl, {
            method: 'GET',
            credentials: 'include',
          });

          if (!response.ok) {
            throw new Error(`Download failed: ${response.statusText}`);
          }

          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${itemTitle}.docx`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);

          console.log(`Downloaded ${itemTitle} as Word successfully`);
          return;
        } catch (error) {
          console.error('Download failed:', error);
          alert(`ダウンロードに失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`);
          return;
        }
      }

        // その他のフォーマットは未対応
        alert('このファイル形式は現在対応していません。');
        return;
      }

      // 超入門版・簡易版はAPI経由でダウンロード
      if (!bcpId) {
        console.error('BCP ID is not available');
        alert('BCP IDが取得できませんでした。');
        return;
      }

      const formatParam = format.toLowerCase() === 'word' ? 'docx' : format.toLowerCase();

      let apiUrl = '';
      if (bcpType === 'introductory') {
        // 超入門版BCP用の専用エンドポイント
        apiUrl = `/api/bcp/${bcpId}/download/introductory?format=${formatParam}`;
      } else if (bcpType === 'simple') {
        // 簡易版BCP用の専用エンドポイント
        apiUrl = `/api/bcp/${bcpId}/download/simple?format=${formatParam}`;
      } else {
        // 他のBCPタイプ用 (既存のexportエンドポイント)
        apiUrl = `/api/bcp/manage/${bcpId}/export?format=${formatParam}`;
      }

      const response = await fetch(apiUrl, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${itemTitle}.${formatParam}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log(`Downloaded ${itemTitle} as ${format} successfully`);
    } catch (error) {
      console.error('Download failed:', error);
      alert(`ダウンロードに失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`);
    } finally {
      setDownloadingItem(null);
    }
  };

  const handleDownloadAll = async () => {
    if (!bcpId) {
      console.error('BCP ID is not available');
      alert('BCP IDが取得できませんでした。');
      return;
    }

    // Check if bcpData is loaded before proceeding (for basic BCP type)
    if (bcpType === 'basic' && !bcpData) {
      console.warn('[BCPDownloadModal] bcpData not loaded yet');
      alert('BCPデータの読み込み中です。しばらくお待ちください。');
      return;
    }

    setIsDownloadingAll(true);

    try {
      console.log('Downloading all files');

      // 基本版の場合、ダウンロード前に未保存のセクションを自動保存
      let finalBcpId = bcpId;
      if (bcpType === 'basic') {
        console.log('[BCPDownloadModal] Auto-saving draft sections before download all...');
        try {
          const tenantId = effectiveTenantId;

          // Log bcpData tenant_id details
          console.log('[BCPDownloadModal] bcpData tenant_id check (download all):', {
            hasBcpData: !!bcpData,
            tenantIdFromUrl,
            tenant_id: bcpData?.tenant_id,
            tenantIdType: typeof bcpData?.tenant_id,
            tenantIdIsNull: bcpData?.tenant_id === null,
            tenantIdIsUndefined: bcpData?.tenant_id === undefined,
            bcpDataKeys: bcpData ? Object.keys(bcpData).slice(0, 10) : [],
            resolvedTenantIdSource: tenantIdFromUrl ? 'url' : 'bcpData',
            resolvedTenantId: tenantId
          });

          // Get default values for all 4 sections
          console.log('[BCPDownloadModal] Getting default values for all sections...');
          const restorePolicyDefaults = getDefaultValuesFromSections(RESTORE_POLICY_SECTIONS);
          const regulationDefaults = getDefaultValuesFromSections(REGULATION_SECTIONS);
          const initialResponseDefaults = getInitialResponseDefaultValues();
          const procedureManualDefaults = getDefaultValuesFromSections(PROCEDURE_MANUAL_SECTIONS);

          console.log('[BCPDownloadModal] Default values extracted:', {
            restore_policy: Object.keys(restorePolicyDefaults).length,
            regulation: Object.keys(regulationDefaults).length,
            initial_response: Object.keys(initialResponseDefaults).length,
            procedure_manual: Object.keys(procedureManualDefaults).length
          });

          // Load from localStorage (user's form input in progress)
          const draftData = loadBasicBcpDraft(bcpId, tenantId);

          // Log localStorage loading details
          const normalizedTenantId = tenantId === 'undefined' || tenantId === null || tenantId === '' ? 'default' : tenantId;
          console.log('[BCPDownloadModal] Loading localStorage (download all):', {
            bcpId,
            tenantId,
            normalizedTenantId,
            storageKey: `basic_bcp_draft_${bcpId}_${normalizedTenantId}`,
            foundDraft: !!draftData,
            draftSections: draftData ? Object.keys(draftData.sections) : [],
            draftLastUpdated: draftData?.lastUpdated
          });

          // Load from server (already saved data)
          const serverRestorePolicy = bcpData?.content?.sections?.restore_policy || {};
          const serverRegulation = bcpData?.content?.sections?.regulation || {};
          const serverInitialResponse = bcpData?.content?.sections?.initial_response || {};
          const serverProcedureManual = bcpData?.content?.sections?.procedure_manual || {};

          // Get localStorage data
          const localRestorePolicy = draftData?.sections?.restore_policy || {};
          const localRegulation = draftData?.sections?.regulation || {};
          const localInitialResponse = draftData?.sections?.initial_response || {};
          const localProcedureManual = draftData?.sections?.procedure_manual || {};

          // Detailed log for localStorage data (before merge)
          console.log('[BCPDownloadModal] PRE-MERGE localStorage details (download all):', {
            regulation: {
              fieldCount: Object.keys(localRegulation).length,
              fieldNames: Object.keys(localRegulation),
              sampleData: Object.keys(localRegulation).slice(0, 3).reduce((acc, key) => {
                acc[key] = localRegulation[key]?.substring(0, 50) || localRegulation[key];
                return acc;
              }, {} as Record<string, any>)
            },
            procedure_manual: {
              fieldCount: Object.keys(localProcedureManual).length,
              fieldNames: Object.keys(localProcedureManual),
              sampleData: Object.keys(localProcedureManual).slice(0, 3).reduce((acc, key) => {
                acc[key] = localProcedureManual[key]?.substring(0, 50) || localProcedureManual[key];
                return acc;
              }, {} as Record<string, any>)
            }
          });

          // Log merge sources for debugging
          console.log('[BCPDownloadModal] 3-way merge sources (download all):', {
            hasBcpData: !!bcpData,
            serverKeys: {
              restore_policy: Object.keys(serverRestorePolicy).length,
              regulation: Object.keys(serverRegulation).length,
              initial_response: Object.keys(serverInitialResponse).length,
              procedure_manual: Object.keys(serverProcedureManual).length
            },
            localKeys: {
              restore_policy: Object.keys(localRestorePolicy).length,
              regulation: Object.keys(localRegulation).length,
              initial_response: Object.keys(localInitialResponse).length,
              procedure_manual: Object.keys(localProcedureManual).length
            },
            defaultKeys: {
              restore_policy: Object.keys(restorePolicyDefaults).length,
              regulation: Object.keys(regulationDefaults).length,
              initial_response: Object.keys(initialResponseDefaults).length,
              procedure_manual: Object.keys(procedureManualDefaults).length
            }
          });

          // Merge with priority: localStorage > server > defaults
          const mergedRestorePolicy = { ...restorePolicyDefaults, ...serverRestorePolicy, ...localRestorePolicy };
          const mergedRegulation = { ...regulationDefaults, ...serverRegulation, ...localRegulation };
          const mergedInitialResponse = { ...initialResponseDefaults, ...serverInitialResponse, ...localInitialResponse };
          const mergedProcedureManual = { ...procedureManualDefaults, ...serverProcedureManual, ...localProcedureManual };

          console.log('[BCPDownloadModal] Merged section data counts:', {
            restore_policy: Object.keys(mergedRestorePolicy).length,
            regulation: Object.keys(mergedRegulation).length,
            initial_response: Object.keys(mergedInitialResponse).length,
            procedure_manual: Object.keys(mergedProcedureManual).length
          });

          // Detailed log for merged data
          console.log('[BCPDownloadModal] POST-MERGE data details (download all):', {
            regulation: {
              fieldCount: Object.keys(mergedRegulation).length,
              fieldNames: Object.keys(mergedRegulation),
              sampleData: Object.keys(mergedRegulation).slice(0, 3).reduce((acc, key) => {
                acc[key] = mergedRegulation[key]?.substring(0, 50) || mergedRegulation[key];
                return acc;
              }, {} as Record<string, any>)
            },
            procedure_manual: {
              fieldCount: Object.keys(mergedProcedureManual).length,
              fieldNames: Object.keys(mergedProcedureManual),
              sampleData: Object.keys(mergedProcedureManual).slice(0, 3).reduce((acc, key) => {
                acc[key] = mergedProcedureManual[key]?.substring(0, 50) || mergedProcedureManual[key];
                return acc;
              }, {} as Record<string, any>)
            }
          });

          // Save all merged data to localStorage with the BCP ID
          saveBasicBcpSection(bcpId, tenantId, 'restore_policy', mergedRestorePolicy);
          saveBasicBcpSection(bcpId, tenantId, 'regulation', mergedRegulation);
          saveBasicBcpSection(bcpId, tenantId, 'initial_response', mergedInitialResponse);
          saveBasicBcpSection(bcpId, tenantId, 'procedure_manual', mergedProcedureManual);

          console.log('[BCPDownloadModal] Saved all sections to localStorage with BCP ID:', bcpId);

          // Verify localStorage immediately after save
          const verifyData = loadBasicBcpDraft(bcpId, tenantId);
          console.log('[BCPDownloadModal] VERIFY localStorage after save (download all):', {
            hasDraft: !!verifyData,
            sections: verifyData ? Object.keys(verifyData.sections) : [],
            sectionFieldCounts: verifyData ? {
              restore_policy: verifyData.sections?.restore_policy ? Object.keys(verifyData.sections.restore_policy).length : 0,
              regulation: verifyData.sections?.regulation ? Object.keys(verifyData.sections.regulation).length : 0,
              initial_response: verifyData.sections?.initial_response ? Object.keys(verifyData.sections.initial_response).length : 0,
              procedure_manual: verifyData.sections?.procedure_manual ? Object.keys(verifyData.sections.procedure_manual).length : 0
            } : null
          });

          finalBcpId = await saveAllDraftSections(
            bcpId,
            tenantId,
            bcpService
          );
          console.log('[BCPDownloadModal] Auto-save completed, final BCP ID:', finalBcpId);

          // 【重要】bcpDataを再取得して連続ダウンロード時のデータ巻き戻しを防止
          // 現象: saveAllDraftSections完了後、localStorageがクリアされる。
          //       その状態で2回目のダウンロードを実行すると、空のlocalStorage + 古いbcpData で
          //       3-way mergeが行われ、最新の保存内容が古いデータで上書きされてしまう。
          // 修正: 保存完了後にサーバーから最新のBCPデータを取得してbcpDataを更新することで、
          //       次回のダウンロード時に最新のサーバーデータを使用できるようにする。
          console.log('[BCPDownloadModal] Re-fetching BCP data after auto-save...');
          const updatedData = await bcpService.getBCP(finalBcpId);
          setBcpData(updatedData);
          console.log('[BCPDownloadModal] bcpData refreshed after auto-save');
        } catch (error) {
          console.error('[BCPDownloadModal] Auto-save failed:', error);
          // 保存に失敗してもダウンロードは続行（保存されているデータだけでもダウンロードできるように）
          console.warn('[BCPDownloadModal] Proceeding with download despite auto-save failure');
        }
      }

      let apiUrl = '';
      let filename = '';

      if (bcpType === 'introductory') {
        // 超入門版BCP用の一括ダウンロードエンドポイント
        apiUrl = `/api/bcp/${finalBcpId}/download/introductory/all`;
        filename = '超入門版BCPシート.zip';
      } else if (bcpType === 'simple') {
        // 簡易版BCP用の一括ダウンロードエンドポイント
        apiUrl = `/api/bcp/${finalBcpId}/download/simple/all`;
        filename = '簡易版BCPシート.zip';
      } else if (bcpType === 'basic') {
        // 基本版BCP用の一括ダウンロードエンドポイント
        apiUrl = `/api/bcp/${finalBcpId}/download/basic/all`;
        filename = '基本版BCPシート.zip';
      } else {
        alert('一括ダウンロードに対応していないBCPタイプです。');
        return;
      }

      const response = await fetch(apiUrl, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log(`Downloaded all files as ${filename} successfully`);
    } catch (error) {
      console.error('Download all files failed:', error);
      alert(`一括ダウンロードに失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`);
    } finally {
      setIsDownloadingAll(false);
    }
  };

  const handleDownloadSummary = async () => {
    if (!bcpId) {
      console.error('BCP ID is not available');
      alert('BCP IDが取得できませんでした。');
      return;
    }

    // Check if bcpData is loaded before proceeding
    if (!bcpData) {
      console.warn('[BCPDownloadModal] bcpData not loaded yet');
      alert('BCPデータの読み込み中です。しばらくお待ちください。');
      return;
    }

    setIsDownloadingSummary(true);

    try {
      console.log('Downloading summary PDF from API');

      // ダウンロード前に未保存のセクションを自動保存
      let finalBcpId = bcpId;
      console.log('[BCPDownloadModal] Auto-saving draft sections before summary download...');
      try {
        const tenantId = effectiveTenantId;

        // Log bcpData tenant_id details
        console.log('[BCPDownloadModal] bcpData tenant_id check (summary PDF):', {
          hasBcpData: !!bcpData,
          tenantIdFromUrl,
          tenant_id: bcpData?.tenant_id,
          tenantIdType: typeof bcpData?.tenant_id,
          tenantIdIsNull: bcpData?.tenant_id === null,
          tenantIdIsUndefined: bcpData?.tenant_id === undefined,
          bcpDataKeys: bcpData ? Object.keys(bcpData).slice(0, 10) : [],
          resolvedTenantIdSource: tenantIdFromUrl ? 'url' : 'bcpData',
          resolvedTenantId: tenantId
        });

        // Get default values for all 4 sections
        console.log('[BCPDownloadModal] Getting default values for all sections...');
        const restorePolicyDefaults = getDefaultValuesFromSections(RESTORE_POLICY_SECTIONS);
        const regulationDefaults = getDefaultValuesFromSections(REGULATION_SECTIONS);
        const initialResponseDefaults = getInitialResponseDefaultValues();
        const procedureManualDefaults = getDefaultValuesFromSections(PROCEDURE_MANUAL_SECTIONS);

        console.log('[BCPDownloadModal] Default values extracted:', {
          restore_policy: Object.keys(restorePolicyDefaults).length,
          regulation: Object.keys(regulationDefaults).length,
          initial_response: Object.keys(initialResponseDefaults).length,
          procedure_manual: Object.keys(procedureManualDefaults).length
        });

        // Load from localStorage (user's form input in progress)
        const draftData = loadBasicBcpDraft(bcpId, tenantId);

        // Log localStorage loading details
        const normalizedTenantId = tenantId === 'undefined' || tenantId === null || tenantId === '' ? 'default' : tenantId;
        console.log('[BCPDownloadModal] Loading localStorage (summary PDF):', {
          bcpId,
          tenantId,
          normalizedTenantId,
          storageKey: `basic_bcp_draft_${bcpId}_${normalizedTenantId}`,
          foundDraft: !!draftData,
          draftSections: draftData ? Object.keys(draftData.sections) : [],
          draftLastUpdated: draftData?.lastUpdated
        });

        // Load from server (already saved data)
        const serverRestorePolicy = bcpData?.content?.sections?.restore_policy || {};
        const serverRegulation = bcpData?.content?.sections?.regulation || {};
        const serverInitialResponse = bcpData?.content?.sections?.initial_response || {};
        const serverProcedureManual = bcpData?.content?.sections?.procedure_manual || {};

        // Get localStorage data
        const localRestorePolicy = draftData?.sections?.restore_policy || {};
        const localRegulation = draftData?.sections?.regulation || {};
        const localInitialResponse = draftData?.sections?.initial_response || {};
        const localProcedureManual = draftData?.sections?.procedure_manual || {};

        // Detailed log for localStorage data (before merge)
        console.log('[BCPDownloadModal] PRE-MERGE localStorage details (summary PDF):', {
          regulation: {
            fieldCount: Object.keys(localRegulation).length,
            fieldNames: Object.keys(localRegulation),
            sampleData: Object.keys(localRegulation).slice(0, 3).reduce((acc, key) => {
              acc[key] = localRegulation[key]?.substring(0, 50) || localRegulation[key];
              return acc;
            }, {} as Record<string, any>)
          },
          procedure_manual: {
            fieldCount: Object.keys(localProcedureManual).length,
            fieldNames: Object.keys(localProcedureManual),
            sampleData: Object.keys(localProcedureManual).slice(0, 3).reduce((acc, key) => {
              acc[key] = localProcedureManual[key]?.substring(0, 50) || localProcedureManual[key];
              return acc;
            }, {} as Record<string, any>)
          }
        });

        // Log merge sources for debugging
        console.log('[BCPDownloadModal] 3-way merge sources (summary PDF):', {
          hasBcpData: !!bcpData,
          serverKeys: {
            restore_policy: Object.keys(serverRestorePolicy).length,
            regulation: Object.keys(serverRegulation).length,
            initial_response: Object.keys(serverInitialResponse).length,
            procedure_manual: Object.keys(serverProcedureManual).length
          },
          localKeys: {
            restore_policy: Object.keys(localRestorePolicy).length,
            regulation: Object.keys(localRegulation).length,
            initial_response: Object.keys(localInitialResponse).length,
            procedure_manual: Object.keys(localProcedureManual).length
          },
          defaultKeys: {
            restore_policy: Object.keys(restorePolicyDefaults).length,
            regulation: Object.keys(regulationDefaults).length,
            initial_response: Object.keys(initialResponseDefaults).length,
            procedure_manual: Object.keys(procedureManualDefaults).length
          }
        });

        // Merge with priority: localStorage > server > defaults
        const mergedRestorePolicy = { ...restorePolicyDefaults, ...serverRestorePolicy, ...localRestorePolicy };
        const mergedRegulation = { ...regulationDefaults, ...serverRegulation, ...localRegulation };
        const mergedInitialResponse = { ...initialResponseDefaults, ...serverInitialResponse, ...localInitialResponse };
        const mergedProcedureManual = { ...procedureManualDefaults, ...serverProcedureManual, ...localProcedureManual };

        console.log('[BCPDownloadModal] Merged section data counts:', {
          restore_policy: Object.keys(mergedRestorePolicy).length,
          regulation: Object.keys(mergedRegulation).length,
          initial_response: Object.keys(mergedInitialResponse).length,
          procedure_manual: Object.keys(mergedProcedureManual).length
        });

        // Detailed log for merged data
        console.log('[BCPDownloadModal] POST-MERGE data details (summary PDF):', {
          regulation: {
            fieldCount: Object.keys(mergedRegulation).length,
            fieldNames: Object.keys(mergedRegulation),
            sampleData: Object.keys(mergedRegulation).slice(0, 3).reduce((acc, key) => {
              acc[key] = mergedRegulation[key]?.substring(0, 50) || mergedRegulation[key];
              return acc;
            }, {} as Record<string, any>)
          },
          procedure_manual: {
            fieldCount: Object.keys(mergedProcedureManual).length,
            fieldNames: Object.keys(mergedProcedureManual),
            sampleData: Object.keys(mergedProcedureManual).slice(0, 3).reduce((acc, key) => {
              acc[key] = mergedProcedureManual[key]?.substring(0, 50) || mergedProcedureManual[key];
              return acc;
            }, {} as Record<string, any>)
          }
        });

        // Save all merged data to localStorage with the BCP ID
        saveBasicBcpSection(bcpId, tenantId, 'restore_policy', mergedRestorePolicy);
        saveBasicBcpSection(bcpId, tenantId, 'regulation', mergedRegulation);
        saveBasicBcpSection(bcpId, tenantId, 'initial_response', mergedInitialResponse);
        saveBasicBcpSection(bcpId, tenantId, 'procedure_manual', mergedProcedureManual);

        console.log('[BCPDownloadModal] Saved all sections to localStorage with BCP ID:', bcpId);

        // Verify localStorage immediately after save
        const verifyData = loadBasicBcpDraft(bcpId, tenantId);
        console.log('[BCPDownloadModal] VERIFY localStorage after save (summary PDF):', {
          hasDraft: !!verifyData,
          sections: verifyData ? Object.keys(verifyData.sections) : [],
          sectionFieldCounts: verifyData ? {
            restore_policy: verifyData.sections?.restore_policy ? Object.keys(verifyData.sections.restore_policy).length : 0,
            regulation: verifyData.sections?.regulation ? Object.keys(verifyData.sections.regulation).length : 0,
            initial_response: verifyData.sections?.initial_response ? Object.keys(verifyData.sections.initial_response).length : 0,
            procedure_manual: verifyData.sections?.procedure_manual ? Object.keys(verifyData.sections.procedure_manual).length : 0
          } : null
        });

        finalBcpId = await saveAllDraftSections(
          bcpId,
          tenantId,
          bcpService
        );
        console.log('[BCPDownloadModal] Auto-save completed, final BCP ID:', finalBcpId);

        // 【重要】bcpDataを再取得して連続ダウンロード時のデータ巻き戻しを防止
        // 現象: saveAllDraftSections完了後、localStorageがクリアされる。
        //       その状態で2回目のダウンロードを実行すると、空のlocalStorage + 古いbcpData で
        //       3-way mergeが行われ、最新の保存内容が古いデータで上書きされてしまう。
        // 修正: 保存完了後にサーバーから最新のBCPデータを取得してbcpDataを更新することで、
        //       次回のダウンロード時に最新のサーバーデータを使用できるようにする。
        console.log('[BCPDownloadModal] Re-fetching BCP data after auto-save...');
        const updatedData = await bcpService.getBCP(finalBcpId);
        setBcpData(updatedData);
        console.log('[BCPDownloadModal] bcpData refreshed after auto-save');
      } catch (error) {
        console.error('[BCPDownloadModal] Auto-save failed:', error);
        console.warn('[BCPDownloadModal] Proceeding with download despite auto-save failure');
      }

      const apiUrl = `/api/bcp/${finalBcpId}/download/basic/summary`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = '基本版BCP(概要版).pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log('Downloaded summary PDF successfully');
    } catch (error) {
      console.error('Summary PDF download failed:', error);
      alert(`ダウンロードに失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`);
    } finally {
      setIsDownloadingSummary(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow:
            '0px 20px 25px -5px rgba(0,0,0,0.1), 0px 8px 10px -6px rgba(0,0,0,0.1)',
          maxWidth: '896px',
          width: '100%',
          maxHeight: bcpType === 'basic' ? '90vh' : '706px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            borderBottom: '1px solid #E5E7EB',
            padding: bcpType === 'basic' ? '16px 20px' : '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h2
            style={{
              fontFamily: 'Noto Sans JP, sans-serif',
              fontWeight: 700,
              fontSize: bcpType === 'basic' ? '18px' : '20px',
              lineHeight: bcpType === 'basic' ? '24px' : '28px',
              color: '#0D357F',
              margin: 0,
            }}
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6B7280',
              fontSize: '24px',
              lineHeight: '1',
              fontWeight: '300',
            }}
            aria-label="閉じる"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: bcpType === 'basic' ? '16px 20px' : '24px',
          }}
        >
          <div
            style={{
              fontFamily: 'Noto Sans JP, sans-serif',
              fontWeight: 350,
              fontSize: bcpType === 'basic' ? '12px' : '14px',
              lineHeight: bcpType === 'basic' ? '18px' : '20px',
              color: '#4B5563',
              marginBottom: bcpType === 'basic' ? '16px' : '24px',
            }}
          >
            作成したBCPの各書類をダウンロードできます。必要な形式を選択してダウンロードしてください。
            <br />
            <br />
            ※BCPの内容を更新する場合は、トップページの「作成済みBCP一覧」から対象のBCPを選択して編集できます。
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: bcpType === 'basic' ? '10px' : '16px' }}>
            {items.map((item, index) => (
              <div
                key={index}
                style={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: bcpType === 'basic' ? '12px' : '17px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: bcpType === 'basic' ? '2px' : '4px' }}>
                  <h3
                    style={{
                      fontFamily: 'Noto Sans JP, sans-serif',
                      fontWeight: 700,
                      fontSize: bcpType === 'basic' ? '14px' : '16px',
                      lineHeight: bcpType === 'basic' ? '20px' : '24px',
                      color: '#111827',
                      margin: 0,
                    }}
                  >
                    {item.title}
                  </h3>
                  <p
                    style={{
                      fontFamily: 'Noto Sans JP, sans-serif',
                      fontWeight: 350,
                      fontSize: bcpType === 'basic' ? '12px' : '14px',
                      lineHeight: bcpType === 'basic' ? '18px' : '20px',
                      color: '#4B5563',
                      margin: 0,
                    }}
                  >
                    {item.description}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
                  {item.formats.map((format, formatIndex) => {
                    const downloadKey = `${item.title}-${format.type}`;
                    const isDownloading = downloadingItem === downloadKey;
                    const itemCreated = isItemCreated(item);

                    return (
                      <button
                        key={formatIndex}
                        onClick={() => handleDownload(item.title, format.type, item)}
                        disabled={!itemCreated || isDownloading || downloadingItem !== null}
                        style={{
                          backgroundColor: !itemCreated ? '#9CA3AF' : format.color,
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '4px 12px',
                          fontFamily: 'Noto Sans JP, sans-serif',
                          fontWeight: 350,
                          fontSize: format.type === 'PDF' ? '12px' : '11.625px',
                          lineHeight: '16px',
                          cursor: !itemCreated || isDownloading || downloadingItem !== null ? 'not-allowed' : 'pointer',
                          opacity: !itemCreated ? 0.5 : (isDownloading || downloadingItem !== null ? 0.7 : 1),
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        {isDownloading && <Spinner size="sm" color="white" inline />}
                        {format.type}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            borderTop: '1px solid #E5E7EB',
            padding: bcpType === 'basic' ? '16px 20px' : '24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <button
            onClick={handleDownloadAll}
            disabled={isDownloadingAll || downloadingItem !== null}
            style={{
              backgroundColor: '#0D357F',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: bcpType === 'basic' ? '10px 20px' : '12px 24px',
              fontFamily: 'Noto Sans JP, sans-serif',
              fontWeight: 700,
              fontSize: '16px',
              lineHeight: '24px',
              cursor: isDownloadingAll || downloadingItem !== null ? 'not-allowed' : 'pointer',
              opacity: isDownloadingAll || downloadingItem !== null ? 0.7 : 1,
              maxWidth: '448px',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            {isDownloadingAll && <Spinner size="sm" color="white" inline />}
            {isDownloadingAll ? 'ダウンロード中...' : 'すべてのファイルを一括ダウンロード'}
          </button>
          {bcpType === 'basic' && (
            <>
              <p
                style={{
                  fontFamily: 'Noto Sans JP, sans-serif',
                  fontWeight: 350,
                  fontSize: '11px',
                  lineHeight: '16px',
                  color: '#6B7280',
                  textAlign: 'center',
                  margin: 0,
                }}
              >
                ※概要版PDFは基本版BCPから主要な項目を抽出したものです。一括ダウンロードには含まれません。
              </p>
              <button
                onClick={handleDownloadSummary}
                disabled={isDownloadingSummary || downloadingItem !== null || isDownloadingAll}
                style={{
                  backgroundColor: '#E60012',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontFamily: 'Noto Sans JP, sans-serif',
                  fontWeight: 700,
                  fontSize: '16px',
                  lineHeight: '24px',
                  cursor: isDownloadingSummary || downloadingItem !== null || isDownloadingAll ? 'not-allowed' : 'pointer',
                  opacity: isDownloadingSummary || downloadingItem !== null || isDownloadingAll ? 0.7 : 1,
                  maxWidth: '448px',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                {isDownloadingSummary && <Spinner size="sm" color="white" inline />}
                {isDownloadingSummary ? 'ダウンロード中...' : '概要版 (PDF) をダウンロード'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BCPDownloadModal;
